/**
 * Workforce API client with error handling and retry logic
 * @module api/workforce
 */

import { get, post, put, del } from '@/api/lib/amplify-wrapper'
import { withRetry } from '@/utils/retry'
import { categorizeError, formatErrorForLogging } from '@/utils/error-handler'
import type {
  Employee,
  Department,
  Role,
  PayrollCost,
  CreateEmployeeData,
  CreateDepartmentData,
  CreateRoleData,
  CreatePayrollCostData
} from '@/types/workforce'
import {
  createEmployeeSchema,
  updateEmployeeSchema,
  createDepartmentSchema,
  updateDepartmentSchema,
  createRoleSchema,
  updateRoleSchema,
  createPayrollCostSchema
} from '@/lib/validators/workforce-schemas'
import { formatZodError, getAllErrorMessages } from '@/locales/pt-BR/validation'
import { createImpersonatedApiHeaders } from '@/lib/services/api-utils'

const API_NAME = 'workforce'

// Debug logger (would use debug package in production)
const logger = console.log

/**
 * Helper function to serialize data for API requests
 */
const serializeData = (data: any) => {
  return JSON.parse(
    JSON.stringify(data, (_key, value) => {
      if (value instanceof Date) {
        return value.toISOString()
      }
      return value
    })
  )
}

// ============================================================================
// EMPLOYEE API FUNCTIONS
// ============================================================================

/**
 * List employees with optional filtering
 */
export const listEmployees = async (
  tenantDomain: string,
  params?: {
    departmentId?: string
    status?: 'ACTIVE' | 'INACTIVE' | 'TERMINATED'
    limit?: number
    nextToken?: string
    page?: number
  }
): Promise<{
  items: Employee[]
  pagination: {
    count: number
    limit: number
    hasNextPage: boolean
    nextToken?: string
  }
}> => {
  try {
    return await withRetry(async () => {
      const queryParams = new URLSearchParams()

      if (params?.departmentId)
        queryParams.append('departmentId', params.departmentId)
      if (params?.status) queryParams.append('status', params.status)
      if (params?.limit) queryParams.append('limit', params.limit.toString())
      if (params?.nextToken) queryParams.append('nextToken', params.nextToken)
      if (params?.page) queryParams.append('page', params.page.toString())

      const path = `/workforce/employees${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
      console.log('>>> [API] listEmployees CALLING:', path, 'params:', params)
      const restOperation = get({
        apiName: API_NAME,
        path,
        options: {
          headers: createImpersonatedApiHeaders()
        }
      })

      const response = await restOperation.response
      const data = (await response.body.json()) as any

      console.log('>>> [API] listEmployees RAW response:', data)

      const nextToken = response.headers?.['x-next-token']
      const itemCount = response.headers?.['x-item-count']

      const items = (data as unknown) as Employee[]

      console.log('>>> [API] listEmployees items:', items.length, items)

      return {
        items,
        pagination: {
          count: itemCount ? parseInt(itemCount) : items.length,
          limit: parseInt(params?.limit?.toString() || '20'),
          hasNextPage: !!nextToken,
          nextToken: nextToken || undefined
        }
      }
    })
  } catch (error) {
    const logData = formatErrorForLogging(error)
    logger('Error listing employees1:', logData)
    throw error
  }
}

/**
 * Get a single employee by ID
 */
export const getEmployee = async (
  tenantDomain: string,
  employeeId: string
): Promise<Employee> => {
  try {
    return await withRetry(async () => {
      const path = `/workforce/employees/${employeeId}`

      const restOperation = get({
        apiName: API_NAME,
        path,
        options: {
          headers: createImpersonatedApiHeaders()
        }
      })

      const response = await restOperation.response
      const data = await response.body.json()
      return (data as unknown) as Employee
    })
  } catch (error) {
    const logData = formatErrorForLogging(error)
    logger('Error getting employee:', logData)
    throw error
  }
}

/**
 * Create a new employee
 */
export const createEmployee = async (
  tenantDomain: string,
  employeeData: CreateEmployeeData
): Promise<Employee> => {
  try {
    // Validate data before sending to API
    const validationResult = createEmployeeSchema.safeParse(employeeData)

    if (!validationResult.success) {
      const errors = formatZodError(validationResult.error)
      const errorMessages = getAllErrorMessages(validationResult.error)
      logger('Employee creation validation failed:', errors)
      throw new Error(`Validação falhou: ${errorMessages.join(', ')}`)
    }

    // Use validated data (type-safe)
    const validatedData = validationResult.data

    // Note: Create operations are NOT retried to avoid duplicate creation
    const path = '/workforce/employees'

    const restOperation = post({
      apiName: API_NAME,
      path,
      options: {
        body: serializeData(validatedData),
        headers: createImpersonatedApiHeaders()
      }
    })

    const response = await restOperation.response
    const data = await response.body.json()
    return (data as unknown) as Employee
  } catch (error) {
    const logData = formatErrorForLogging(error)
    logger('Error creating employee:', logData)
    throw error
  }
}

/**
 * Update an existing employee
 */
export const updateEmployee = async (
  tenantDomain: string,
  employeeId: string,
  updates: Partial<Employee>
): Promise<Employee> => {
  try {
    // Validate update data before sending to API
    const validationResult = updateEmployeeSchema.safeParse(updates)

    if (!validationResult.success) {
      const errors = formatZodError(validationResult.error)
      const errorMessages = getAllErrorMessages(validationResult.error)
      logger('Employee update validation failed:', errors)
      throw new Error(`Validação falhou: ${errorMessages.join(', ')}`)
    }

    // Use validated data (type-safe)
    const validatedData = validationResult.data

    // Note: Update operations are NOT retried to avoid duplicate updates
    const path = `/workforce/employees/${employeeId}`

    const restOperation = put({
      apiName: API_NAME,
      path,
      options: {
        body: serializeData(validatedData),
        headers: createImpersonatedApiHeaders()
      }
    })

    const response = await restOperation.response
    const data = await response.body.json()
    return (data as unknown) as Employee
  } catch (error) {
    const logData = formatErrorForLogging(error)
    logger('Error updating employee:', logData)
    throw error
  }
}

/**
 * Delete an employee
 */
export const deleteEmployee = async (
  tenantDomain: string,
  employeeId: string
): Promise<void> => {
  try {
    const path = `/workforce/employees/${employeeId}`

    const restOperation = del({
      apiName: API_NAME,
      path,
      options: {
        headers: createImpersonatedApiHeaders()
      }
    })

    await restOperation.response
  } catch (error) {
    const logData = formatErrorForLogging(error)
    logger('Error deleting employee:', logData)
    throw error
  }
}

// ============================================================================
// DEPARTMENT API FUNCTIONS
// ============================================================================

/**
 * List all departments
 */
export const listDepartments = async (
  tenantDomain: string
): Promise<Department[]> => {
  try {
    return await withRetry(async () => {
      const path = '/workforce/departments'

      const restOperation = get({
        apiName: API_NAME,
        path,
        options: {
          headers: createImpersonatedApiHeaders()
        }
      })

      const response = await restOperation.response
      const data = await response.body.json()
      // API returns array directly
      return (data as unknown) as Department[]
    })
  } catch (error) {
    const logData = formatErrorForLogging(error)
    logger('Error listing departments:', logData)
    throw error
  }
}

/**
 * Get a single department by ID
 */
export const getDepartment = async (
  tenantDomain: string,
  departmentId: string
): Promise<Department> => {
  try {
    return await withRetry(async () => {
      const path = `/workforce/departments/${departmentId}`

      const restOperation = get({
        apiName: API_NAME,
        path,
        options: {
          headers: createImpersonatedApiHeaders()
        }
      })

      const response = await restOperation.response
      const data = await response.body.json()
      return (data as unknown) as Department
    })
  } catch (error) {
    const logData = formatErrorForLogging(error)
    logger('Error getting department:', logData)
    throw error
  }
}

/**
 * Create a new department
 */
export const createDepartment = async (
  tenantDomain: string,
  departmentData: CreateDepartmentData
): Promise<Department> => {
  try {
    // Validate data before sending to API
    const validationResult = createDepartmentSchema.safeParse(departmentData)

    if (!validationResult.success) {
      const errors = formatZodError(validationResult.error)
      const errorMessages = getAllErrorMessages(validationResult.error)
      logger('Department creation validation failed:', errors)
      throw new Error(`Validação falhou: ${errorMessages.join(', ')}`)
    }

    const path = '/workforce/departments'

    const restOperation = post({
      apiName: API_NAME,
      path,
      options: {
        body: serializeData(validationResult.data),
        headers: createImpersonatedApiHeaders()
      }
    })

    const response = await restOperation.response
    const data = await response.body.json()
    return (data as unknown) as Department
  } catch (error) {
    const logData = formatErrorForLogging(error)
    logger('Error creating department:', logData)
    throw error
  }
}

/**
 * Update an existing department
 */
export const updateDepartment = async (
  tenantDomain: string,
  departmentId: string,
  updates: Partial<Department>
): Promise<Department> => {
  try {
    // Validate update data before sending to API
    const validationResult = updateDepartmentSchema.safeParse(updates)

    if (!validationResult.success) {
      const errors = formatZodError(validationResult.error)
      const errorMessages = getAllErrorMessages(validationResult.error)
      logger('Department update validation failed:', errors)
      throw new Error(`Validação falhou: ${errorMessages.join(', ')}`)
    }

    const path = `/workforce/departments/${departmentId}`

    const restOperation = put({
      apiName: API_NAME,
      path,
      options: {
        body: serializeData(validationResult.data),
        headers: createImpersonatedApiHeaders()
      }
    })

    const response = await restOperation.response
    const data = await response.body.json()
    return (data as unknown) as Department
  } catch (error) {
    const logData = formatErrorForLogging(error)
    logger('Error updating department:', logData)
    throw error
  }
}

// ============================================================================
// ROLE API FUNCTIONS
// ============================================================================

/**
 * List all roles
 */
export const listRoles = async (
  tenantDomain: string
): Promise<{
  roles: Role[]
  pagination?: {
    count: number
    hasNextPage: boolean
    nextToken?: string
  }
}> => {
  try {
    return await withRetry(async () => {
      const path = '/workforce/roles'

      const restOperation = get({
        apiName: API_NAME,
        path,
        options: {
          headers: createImpersonatedApiHeaders()
        }
      })

      const response = await restOperation.response
      const data = await response.body.json()
      const roles = (data as unknown) as Role[]

      return {
        roles,
        pagination: {
          count: roles.length,
          hasNextPage: false
        }
      }
    })
  } catch (error) {
    const logData = formatErrorForLogging(error)
    logger('Error listing roles:', logData)
    throw error
  }
}

/**
 * Get a single role by ID
 */
export const getRole = async (
  tenantDomain: string,
  roleId: string
): Promise<Role> => {
  try {
    return await withRetry(async () => {
      const path = `/workforce/roles/${roleId}`

      const restOperation = get({
        apiName: API_NAME,
        path,
        options: {
          headers: createImpersonatedApiHeaders()
        }
      })

      const response = await restOperation.response
      const data = await response.body.json()
      return (data as unknown) as Role
    })
  } catch (error) {
    const logData = formatErrorForLogging(error)
    logger('Error getting role:', logData)
    throw error
  }
}

/**
 * Create a new role
 */
export const createRole = async (
  tenantDomain: string,
  roleData: CreateRoleData
): Promise<Role> => {
  try {
    // Validate data before sending to API
    const validationResult = createRoleSchema.safeParse(roleData)

    if (!validationResult.success) {
      const errors = formatZodError(validationResult.error)
      const errorMessages = getAllErrorMessages(validationResult.error)
      logger('Role creation validation failed:', errors)
      throw new Error(`Validação falhou: ${errorMessages.join(', ')}`)
    }

    const path = '/workforce/roles'

    const restOperation = post({
      apiName: API_NAME,
      path,
      options: {
        body: serializeData(validationResult.data),
        headers: createImpersonatedApiHeaders()
      }
    })

    const response = await restOperation.response
    const data = await response.body.json()
    return (data as unknown) as Role
  } catch (error) {
    const logData = formatErrorForLogging(error)
    logger('Error creating role:', logData)
    throw error
  }
}

/**
 * Update an existing role
 */
export const updateRole = async (
  tenantDomain: string,
  roleId: string,
  updates: Partial<Role>
): Promise<Role> => {
  try {
    // Validate update data before sending to API
    const validationResult = updateRoleSchema.safeParse(updates)

    if (!validationResult.success) {
      const errors = formatZodError(validationResult.error)
      const errorMessages = getAllErrorMessages(validationResult.error)
      logger('Role update validation failed:', errors)
      throw new Error(`Validação falhou: ${errorMessages.join(', ')}`)
    }

    const path = `/workforce/roles/${roleId}`

    const restOperation = put({
      apiName: API_NAME,
      path,
      options: {
        body: serializeData(validationResult.data),
        headers: createImpersonatedApiHeaders()
      }
    })

    const response = await restOperation.response
    const data = await response.body.json()
    return (data as unknown) as Role
  } catch (error) {
    const logData = formatErrorForLogging(error)
    logger('Error updating role:', logData)
    throw error
  }
}

// ============================================================================
// PAYROLL API FUNCTIONS
// ============================================================================

/**
 * Batch process payroll result with transaction support
 */
export interface BatchPayrollResult {
  processed: number
  failed: number
  totalChunks: number
  successfulChunks: number
  results: PayrollCost[]
  errors: Array<{
    index?: number
    chunkIndex: number
    error: string
    transactionId?: string
  }>
  rolledBack: number
}

/**
 * Batch process payroll for employees with transaction support
 */
export const batchProcessPayroll = async (
  tenantDomain: string,
  payrollRecords: CreatePayrollCostData[]
): Promise<BatchPayrollResult> => {
  try {
    // Validate input
    if (!Array.isArray(payrollRecords) || payrollRecords.length === 0) {
      throw new Error('payrollRecords must be a non-empty array')
    }

    if (payrollRecords.length > 100) {
      throw new Error('Cannot process more than 100 payroll records in a single batch')
    }

    // Validate each record
    payrollRecords.forEach((record, index) => {
      const validationResult = createPayrollCostSchema.safeParse(record)
      if (!validationResult.success) {
        const errors = formatZodError(validationResult.error)
        const errorMessages = getAllErrorMessages(validationResult.error)
        logger(`Payroll record ${index} validation failed:`, errors)
        throw new Error(`Validação falhou no registro ${index}: ${errorMessages.join(', ')}`)
      }
    })

    const path = '/workforce/payroll/batch-process'

    const restOperation = post({
      apiName: API_NAME,
      path,
      options: {
        body: { payrollRecords: payrollRecords.map(r => serializeData(r)) },
        headers: createImpersonatedApiHeaders()
      }
    })

    const response = await restOperation.response
    const data = await response.body.json()
    return (data as unknown) as BatchPayrollResult
  } catch (error) {
    const logData = formatErrorForLogging(error)
    logger('Error batch processing payroll:', logData)
    throw error
  }
}

/**
 * List payroll costs with optional filtering
 */
export const listPayrollCosts = async (
  tenantDomain: string,
  params?: {
    departmentId?: number
    startDate?: string
    endDate?: string
    limit?: number
    nextToken?: string
  }
): Promise<{
  items: PayrollCost[]
  pagination: {
    count: number
    limit: number
    hasNextPage: boolean
    nextToken?: string
  }
}> => {
  try {
    return await withRetry(async () => {
      const queryParams = new URLSearchParams()

      if (params?.departmentId)
        queryParams.append('departmentId', params.departmentId.toString())
      if (params?.startDate) queryParams.append('startDate', params.startDate)
      if (params?.endDate) queryParams.append('endDate', params.endDate)
      if (params?.limit) queryParams.append('limit', params.limit.toString())
      if (params?.nextToken) queryParams.append('nextToken', params.nextToken)

      const path = `/workforce/payroll/costs${queryParams.toString() ? `?${queryParams.toString()}` : ''}`

      const restOperation = get({
        apiName: API_NAME,
        path,
        options: {
          headers: createImpersonatedApiHeaders()
        }
      })

      const response = await restOperation.response
      const data = await response.body.json()

      const nextToken = response.headers?.['x-next-token']
      const itemCount = response.headers?.['x-item-count']

      const items = (data as unknown) as PayrollCost[]

      return {
        items,
        pagination: {
          count: itemCount ? parseInt(itemCount) : items.length,
          limit: parseInt(params?.limit?.toString() || '20'),
          hasNextPage: !!nextToken,
          nextToken: nextToken || undefined
        }
      }
    })
  } catch (error) {
    const logData = formatErrorForLogging(error)
    logger('Error listing payroll costs:', logData)
    throw error
  }
}

// ============================================================================
// STATISTICS & DASHBOARD API FUNCTIONS
// ============================================================================

/**
 * Get employee statistics for dashboard
 */
export const getEmployeeStats = async (tenantDomain: string): Promise<any> => {
  try {
    return await withRetry(async () => {
      const path = '/workforce/employees/stats'

      const restOperation = get({
        apiName: API_NAME,
        path,
        options: {
          headers: createImpersonatedApiHeaders()
        }
      })

      const response = await restOperation.response
      const data = await response.body.json()
      return data
    })
  } catch (error) {
    const logData = formatErrorForLogging(error)
    logger('Error getting employee stats:', logData)
    throw error
  }
}

/**
 * Get organizational hierarchy tree
 */
export const getOrganizationalHierarchy = async (
  tenantDomain: string
): Promise<any> => {
  try {
    return await withRetry(async () => {
      const path = '/workforce/hierarchy'

      const restOperation = get({
        apiName: API_NAME,
        path,
        options: {
          headers: createImpersonatedApiHeaders()
        }
      })

      const response = await restOperation.response
      const data = await response.body.json()
      return data
    })
  } catch (error) {
    const logData = formatErrorForLogging(error)
    logger('Error getting organizational hierarchy:', logData)
    throw error
  }
}

/**
 * Get all reports for the current user (manager/director)
 */
export const getMyAllReports = async (params?: {
  limit?: number
  nextToken?: string
}): Promise<{
  items: Employee[]
  pagination: {
    count: number
    limit: number
    hasNextPage: boolean
    nextToken?: string
  }
}> => {
  try {
    return await withRetry(async () => {
      const queryParams = new URLSearchParams()

      if (params?.limit) queryParams.append('limit', params.limit.toString())
      if (params?.nextToken) queryParams.append('nextToken', params.nextToken)

      const path = `/workforce/employees/me/all-reports${queryParams.toString() ? `?${queryParams.toString()}` : ''}`

      const restOperation = get({
        apiName: API_NAME,
        path,
        options: {
          headers: createImpersonatedApiHeaders()
        }
      })

      const response = await restOperation.response
      const data = (await response.body.json()) as any

      const nextToken = response.headers?.['x-next-token']
      const itemCount = response.headers?.['x-item-count']

      const items = (data as unknown) as Employee[]

      return {
        items,
        pagination: {
          count: itemCount ? parseInt(itemCount) : items.length,
          limit: parseInt(params?.limit?.toString() || '100'),
          hasNextPage: !!nextToken,
          nextToken: nextToken || undefined
        }
      }
    })
  } catch (error) {
    const logData = formatErrorForLogging(error)
    logger('Error getting my reports:', logData)
    throw error
  }
}

// ============================================================================
// BULK OPERATIONS
// ============================================================================

export interface BulkOperationResult {
  success: number
  failed: number
  errors: Array<{
    employeeId: string
    name: string
    error: string
  }>
}

/**
 * Bulk update employee status
 * Processes updates in batches to avoid overwhelming the API
 */
export const bulkUpdateEmployeeStatus = async (
  tenantDomain: string,
  employeeIds: string[],
  status: 'ACTIVE' | 'INACTIVE',
  employees: Employee[]
): Promise<BulkOperationResult> => {
  const BATCH_SIZE = 25 // DynamoDB transaction limit
  const result: BulkOperationResult = {
    success: 0,
    failed: 0,
    errors: []
  }

  // Process in batches
  for (let i = 0; i < employeeIds.length; i += BATCH_SIZE) {
    const batch = employeeIds.slice(i, i + BATCH_SIZE)

    // Process batch concurrently
    const results = await Promise.allSettled(
      batch.map(async (employeeId) => {
        const employee = employees.find(emp => emp.employeeId === employeeId)
        try {
          if (!employee?.employment) {
            throw new Error('Employee employment data not found')
          }
          await updateEmployee(tenantDomain, employeeId, {
            employment: {
              ...employee.employment,
              status
            }
          })
          return { employeeId, success: true }
        } catch (error) {
          return {
            employeeId,
            success: false,
            error: error instanceof Error ? error.message : 'Unknown error'
          }
        }
      })
    )

    // Collect results
    results.forEach((promiseResult, index) => {
      const employeeId = batch[index]
      const employee = employees.find(emp => emp.employeeId === employeeId)

      if (promiseResult.status === 'fulfilled' && promiseResult.value.success) {
        result.success++
      } else {
        result.failed++
        result.errors.push({
          employeeId,
          name: employee?.personalInfo?.name || 'Unknown',
          error:
            promiseResult.status === 'fulfilled'
              ? promiseResult.value.error || 'Update failed'
              : 'Promise rejected'
        })
      }
    })
  }

  return result
}

/**
 * Bulk transfer employees to a new department
 */
export const bulkTransferDepartment = async (
  tenantDomain: string,
  employeeIds: string[],
  departmentId: number,
  employees: Employee[]
): Promise<BulkOperationResult> => {
  const BATCH_SIZE = 25
  const result: BulkOperationResult = {
    success: 0,
    failed: 0,
    errors: []
  }

  // Process in batches
  for (let i = 0; i < employeeIds.length; i += BATCH_SIZE) {
    const batch = employeeIds.slice(i, i + BATCH_SIZE)

    const results = await Promise.allSettled(
      batch.map(async (employeeId) => {
        const employee = employees.find(emp => emp.employeeId === employeeId)
        try {
          if (!employee?.employment) {
            throw new Error('Employee employment data not found')
          }
          await updateEmployee(tenantDomain, employeeId, {
            employment: {
              ...employee.employment,
              department: {
                ...employee.employment.department,
                id: departmentId
              }
            }
          })
          return { employeeId, success: true }
        } catch (error) {
          return {
            employeeId,
            success: false,
            error: error instanceof Error ? error.message : 'Unknown error'
          }
        }
      })
    )

    // Collect results
    results.forEach((promiseResult, index) => {
      const employeeId = batch[index]
      const employee = employees.find(emp => emp.employeeId === employeeId)

      if (promiseResult.status === 'fulfilled' && promiseResult.value.success) {
        result.success++
      } else {
        result.failed++
        result.errors.push({
          employeeId,
          name: employee?.personalInfo?.name || 'Unknown',
          error:
            promiseResult.status === 'fulfilled'
              ? promiseResult.value.error || 'Transfer failed'
              : 'Promise rejected'
        })
      }
    })
  }

  return result
}

/**
 * Bulk reassign employees to a new manager
 */
export const bulkReassignManager = async (
  tenantDomain: string,
  employeeIds: string[],
  managerId: string | null,
  employees: Employee[]
): Promise<BulkOperationResult> => {
  const BATCH_SIZE = 25
  const result: BulkOperationResult = {
    success: 0,
    failed: 0,
    errors: []
  }

  // Validate no circular references
  if (managerId && employeeIds.includes(managerId)) {
    result.failed = employeeIds.length
    result.errors = employeeIds.map(id => ({
      employeeId: id,
      name: employees.find(emp => emp.employeeId === id)?.personalInfo?.name || 'Unknown',
      error: 'Cannot assign manager from selected employees (circular reference)'
    }))
    return result
  }

  // Process in batches
  for (let i = 0; i < employeeIds.length; i += BATCH_SIZE) {
    const batch = employeeIds.slice(i, i + BATCH_SIZE)

    const results = await Promise.allSettled(
      batch.map(async (employeeId) => {
        const employee = employees.find(emp => emp.employeeId === employeeId)
        try {
          if (!employee?.employment) {
            throw new Error('Employee employment data not found')
          }
          await updateEmployee(tenantDomain, employeeId, {
            employment: {
              ...employee.employment,
              managerId
            }
          })
          return { employeeId, success: true }
        } catch (error) {
          return {
            employeeId,
            success: false,
            error: error instanceof Error ? error.message : 'Unknown error'
          }
        }
      })
    )

    // Collect results
    results.forEach((promiseResult, index) => {
      const employeeId = batch[index]
      const employee = employees.find(emp => emp.employeeId === employeeId)

      if (promiseResult.status === 'fulfilled' && promiseResult.value.success) {
        result.success++
      } else {
        result.failed++
        result.errors.push({
          employeeId,
          name: employee?.personalInfo?.name || 'Unknown',
          error:
            promiseResult.status === 'fulfilled'
              ? promiseResult.value.error || 'Reassignment failed'
              : 'Promise rejected'
        })
      }
    })
  }

  return result
}

// ============================================================================
// HIERARCHY & TEAM MANAGEMENT API FUNCTIONS
// ============================================================================

/**
 * Employee tree structure for hierarchy visualization
 */
export interface EmployeeTree extends Employee {
  children?: EmployeeTree[]
  reports?: EmployeeTree[]
}

/**
 * Get direct reports for the current user
 */
export const getMyDirectReports = async (params?: {
  limit?: number
  nextToken?: string
}): Promise<{
  items: Employee[]
  pagination: {
    count: number
    limit: number
    hasNextPage: boolean
    nextToken?: string
  }
}> => {
  try {
    return await withRetry(async () => {
      const queryParams = new URLSearchParams()

      if (params?.limit) queryParams.append('limit', params.limit.toString())
      if (params?.nextToken) queryParams.append('nextToken', params.nextToken)

      const path = `/workforce/employees/me/reports${queryParams.toString() ? `?${queryParams.toString()}` : ''}`

      const restOperation = get({
        apiName: API_NAME,
        path,
        options: {
          headers: createImpersonatedApiHeaders()
        }
      })

      const response = await restOperation.response
      const data = (await response.body.json()) as any

      const nextToken = response.headers?.['x-next-token']
      const itemCount = response.headers?.['x-item-count']

      const items = (data as unknown) as Employee[]

      return {
        items,
        pagination: {
          count: itemCount ? parseInt(itemCount) : items.length,
          limit: parseInt(params?.limit?.toString() || '20'),
          hasNextPage: !!nextToken,
          nextToken: nextToken || undefined
        }
      }
    })
  } catch (error) {
    const logData = formatErrorForLogging(error)
    logger('Error getting my direct reports:', logData)
    throw error
  }
}

/**
 * Get management tree for the current user
 */
export const getMyManagementTree = async (params?: {
  includeManager?: boolean
}): Promise<EmployeeTree> => {
  try {
    return await withRetry(async () => {
      const queryParams = new URLSearchParams()

      if (params?.includeManager) queryParams.append('includeManager', 'true')

      const path = `/workforce/employees/me/management-tree${queryParams.toString() ? `?${queryParams.toString()}` : ''}`

      const restOperation = get({
        apiName: API_NAME,
        path,
        options: {
          headers: createImpersonatedApiHeaders()
        }
      })

      const response = await restOperation.response
      const data = await response.body.json()
      return (data as unknown) as EmployeeTree
    })
  } catch (error) {
    const logData = formatErrorForLogging(error)
    logger('Error getting my management tree:', logData)
    throw error
  }
}

/**
 * Get employees by department
 */
export const getEmployeesByDepartment = async (
  tenantDomain: string,
  departmentId: number,
  params?: {
    limit?: number
    nextToken?: string
  }
): Promise<{
  items: Employee[]
  pagination: {
    count: number
    limit: number
    hasNextPage: boolean
    nextToken?: string
  }
}> => {
  try {
    return await withRetry(async () => {
      const queryParams = new URLSearchParams()

      if (params?.limit) queryParams.append('limit', params.limit.toString())
      if (params?.nextToken) queryParams.append('nextToken', params.nextToken)

      const path = `/workforce/departments/${departmentId}/employees${queryParams.toString() ? `?${queryParams.toString()}` : ''}`

      const restOperation = get({
        apiName: API_NAME,
        path,
        options: {
          headers: createImpersonatedApiHeaders()
        }
      })

      const response = await restOperation.response
      const data = (await response.body.json()) as any

      const nextToken = response.headers?.['x-next-token']
      const itemCount = response.headers?.['x-item-count']

      const items = (data as unknown) as Employee[]

      return {
        items,
        pagination: {
          count: itemCount ? parseInt(itemCount) : items.length,
          limit: parseInt(params?.limit?.toString() || '20'),
          hasNextPage: !!nextToken,
          nextToken: nextToken || undefined
        }
      }
    })
  } catch (error) {
    const logData = formatErrorForLogging(error)
    logger('Error getting employees by department:', logData)
    throw error
  }
}

/**
 * Get direct reports for an employee
 */
export const getDirectReports = async (
  tenantDomain: string,
  employeeId: string,
  params?: {
    limit?: number
    nextToken?: string
  }
): Promise<{
  items: Employee[]
  pagination: {
    count: number
    limit: number
    hasNextPage: boolean
    nextToken?: string
  }
}> => {
  try {
    return await withRetry(async () => {
      const queryParams = new URLSearchParams()

      if (params?.limit) queryParams.append('limit', params.limit.toString())
      if (params?.nextToken) queryParams.append('nextToken', params.nextToken)

      const path = `/workforce/employees/${employeeId}/reports${queryParams.toString() ? `?${queryParams.toString()}` : ''}`

      const restOperation = get({
        apiName: API_NAME,
        path,
        options: {
          headers: createImpersonatedApiHeaders()
        }
      })

      const response = await restOperation.response
      const data = (await response.body.json()) as any

      const nextToken = response.headers?.['x-next-token']
      const itemCount = response.headers?.['x-item-count']

      const items = (data as unknown) as Employee[]

      return {
        items,
        pagination: {
          count: itemCount ? parseInt(itemCount) : items.length,
          limit: parseInt(params?.limit?.toString() || '20'),
          hasNextPage: !!nextToken,
          nextToken: nextToken || undefined
        }
      }
    })
  } catch (error) {
    const logData = formatErrorForLogging(error)
    logger('Error getting direct reports:', logData)
    throw error
  }
}

/**
 * Get management tree for an employee
 */
export const getManagementTree = async (
  tenantDomain: string,
  employeeId: string,
  params?: {
    maxDepth?: number
    includeManager?: boolean
  }
): Promise<EmployeeTree> => {
  try {
    return await withRetry(async () => {
      const queryParams = new URLSearchParams()

      if (params?.maxDepth) queryParams.append('maxDepth', params.maxDepth.toString())
      if (params?.includeManager) queryParams.append('includeManager', 'true')

      const path = `/workforce/employees/${employeeId}/management-tree${queryParams.toString() ? `?${queryParams.toString()}` : ''}`

      const restOperation = get({
        apiName: API_NAME,
        path,
        options: {
          headers: createImpersonatedApiHeaders()
        }
      })

      const response = await restOperation.response
      const data = await response.body.json()
      return (data as unknown) as EmployeeTree
    })
  } catch (error) {
    const logData = formatErrorForLogging(error)
    logger('Error getting management tree:', logData)
    throw error
  }
}

/**
 * List employees with scoped access based on user's role/hierarchy
 * This is a convenience wrapper around listEmployees that applies
 * automatic scoping based on the current user's permissions
 */
export const listEmployeesScoped = async (
  tenantDomain: string,
  params?: {
    departmentId?: number
    status?: 'ACTIVE' | 'INACTIVE' | 'TERMINATED'
    limit?: number
    nextToken?: string
  }
): Promise<{
  items: Employee[]
  pagination: {
    count: number
    limit: number
    hasNextPage: boolean
    nextToken?: string
  }
}> => {
  // For now, this is identical to listEmployees
  // In the future, this could apply additional scoping logic based on user role
  return listEmployees(tenantDomain, params ? {
    ...params,
    departmentId: params.departmentId?.toString()
  } : undefined)
}

// ============================================================================
// ADDITIONAL PAYROLL & EMPLOYEE API FUNCTIONS
// ============================================================================

/**
 * Get payroll history for a specific employee
 * @stub This is a stub implementation returning mock data
 */
export const getEmployeePayrollHistory = async (
  tenantDomain: string,
  employeeId: string,
  params?: {
    startDate?: string
    endDate?: string
    limit?: number
    nextToken?: string
  }
): Promise<{
  items: PayrollCost[]
  pagination: {
    count: number
    limit: number
    hasNextPage: boolean
    nextToken?: string
  }
}> => {
  try {
    return await withRetry(async () => {
      const queryParams = new URLSearchParams()

      if (params?.startDate) queryParams.append('startDate', params.startDate)
      if (params?.endDate) queryParams.append('endDate', params.endDate)
      if (params?.limit) queryParams.append('limit', params.limit.toString())
      if (params?.nextToken) queryParams.append('nextToken', params.nextToken)

      const path = `/workforce/employees/${employeeId}/payroll-history${queryParams.toString() ? `?${queryParams.toString()}` : ''}`

      const restOperation = get({
        apiName: API_NAME,
        path,
        options: {
          headers: createImpersonatedApiHeaders()
        }
      })

      const response = await restOperation.response
      const data = await response.body.json()

      const nextToken = response.headers?.['x-next-token']
      const itemCount = response.headers?.['x-item-count']

      const items = (data as unknown) as PayrollCost[]

      return {
        items,
        pagination: {
          count: itemCount ? parseInt(itemCount) : items.length,
          limit: parseInt(params?.limit?.toString() || '20'),
          hasNextPage: !!nextToken,
          nextToken: nextToken || undefined
        }
      }
    })
  } catch (error) {
    const logData = formatErrorForLogging(error)
    logger('Error getting employee payroll history:', logData)
    throw error
  }
}

/**
 * Update a single employee's status using dedicated endpoint
 */
export const updateEmployeeStatus = async (
  tenantDomain: string,
  employeeId: string,
  status: 'ACTIVE' | 'INACTIVE' | 'TERMINATED',
  endDate?: string,
  reason?: string
): Promise<Employee> => {
  try {
    const path = `/workforce/employees/${employeeId}/status`

    const restOperation = put({
      apiName: API_NAME,
      path,
      options: {
        body: {
          status,
          endDate,
          reason
        } as any,
        headers: createImpersonatedApiHeaders()
      }
    })

    const response = await restOperation.response
    const data = await response.body.json()
    return (data as unknown) as Employee
  } catch (error) {
    const logData = formatErrorForLogging(error)
    logger('Error updating employee status:', logData)
    throw error
  }
}

/**
 * Get payroll costs aggregated by period
 * @stub This is a stub implementation returning mock data
 */
export const getPayrollCostsByPeriod = async (
  tenantDomain: string,
  params: {
    startDate: string
    endDate: string
    groupBy?: 'month' | 'quarter' | 'year'
    departmentId?: number
  }
): Promise<{
  periods: Array<{
    period: string
    totalCost: number
    employeeCount: number
    averageCost: number
  }>
}> => {
  try {
    return await withRetry(async () => {
      const queryParams = new URLSearchParams()

      queryParams.append('startDate', params.startDate)
      queryParams.append('endDate', params.endDate)
      if (params.groupBy) queryParams.append('groupBy', params.groupBy)
      if (params.departmentId)
        queryParams.append('departmentId', params.departmentId.toString())

      const path = `/workforce/payroll/costs-by-period?${queryParams.toString()}`

      const restOperation = get({
        apiName: API_NAME,
        path,
        options: {
          headers: createImpersonatedApiHeaders()
        }
      })

      const response = await restOperation.response
      const data = await response.body.json()
      return (data as unknown) as {
        periods: Array<{
          period: string
          totalCost: number
          employeeCount: number
          averageCost: number
        }>
      }
    })
  } catch (error) {
    const logData = formatErrorForLogging(error)
    logger('Error getting payroll costs by period:', logData)
    throw error
  }
}

/**
 * Get payroll totals summarized by period
 * @stub This is a stub implementation returning mock data
 */
export const getPayrollTotalsByPeriod = async (
  tenantDomain: string,
  params: {
    startDate: string
    endDate: string
    departmentId?: number
  }
): Promise<{
  totalGrossSalary: number
  totalNetSalary: number
  totalDeductions: number
  totalBenefits: number
  employeeCount: number
}> => {
  try {
    return await withRetry(async () => {
      const queryParams = new URLSearchParams()

      queryParams.append('startDate', params.startDate)
      queryParams.append('endDate', params.endDate)
      if (params.departmentId)
        queryParams.append('departmentId', params.departmentId.toString())

      const path = `/workforce/payroll/totals-by-period?${queryParams.toString()}`

      const restOperation = get({
        apiName: API_NAME,
        path,
        options: {
          headers: createImpersonatedApiHeaders()
        }
      })

      const response = await restOperation.response
      const data = await response.body.json()
      return (data as unknown) as {
        totalGrossSalary: number
        totalNetSalary: number
        totalDeductions: number
        totalBenefits: number
        employeeCount: number
      }
    })
  } catch (error) {
    const logData = formatErrorForLogging(error)
    logger('Error getting payroll totals by period:', logData)
    throw error
  }
}

// ============================================================================
// EMPLOYEE PICTURE UPLOAD (S3 Presigned URL Pattern)
// ============================================================================

/**
 * Request parameters for generating a presigned upload URL
 */
export interface EmployeePictureUploadParams {
  mimeType: string
  extension: string
  originalFilename: string
  contentLength?: number
}

/**
 * Response from presigned URL generation
 */
export interface EmployeePictureUploadUrlResponse {
  signedUrl: string
  key: string
  bucket: string
  metadata: {
    originalFilename: string
    mimeType: string
    extension: string
    employeeId: string
  }
}

/**
 * Get a presigned URL for uploading an employee picture to S3
 *
 * This follows the secure S3 upload pattern:
 * 1. Frontend requests a presigned URL from backend
 * 2. Backend generates the URL with proper authorization and validation
 * 3. Frontend uploads directly to S3 using the presigned URL
 * 4. Frontend sends the S3 key back to backend to update employee record
 */
export const getEmployeePictureUploadUrl = async (
  employeeId: string,
  params: EmployeePictureUploadParams
): Promise<EmployeePictureUploadUrlResponse> => {
  try {
    const path = `/workforce/employees/${employeeId}/picture/upload-url`

    const restOperation = post({
      apiName: API_NAME,
      path,
      options: {
        body: {
          mimeType: params.mimeType,
          extension: params.extension,
          originalFilename: params.originalFilename,
          contentLength: params.contentLength
        } as any,
        headers: createImpersonatedApiHeaders()
      }
    })

    const response = await restOperation.response
    const data = await response.body.json()
    return (data as unknown) as EmployeePictureUploadUrlResponse
  } catch (error) {
    const logData = formatErrorForLogging(error)
    logger('Error getting employee picture upload URL:', logData)
    throw error
  }
}

/**
 * Upload employee picture file directly to S3 using presigned URL
 *
 * @param signedUrl - The presigned URL from getEmployeePictureUploadUrl
 * @param file - The File object to upload
 */
export const uploadEmployeePictureToS3 = async (
  signedUrl: string,
  file: File
): Promise<void> => {
  try {
    const response = await fetch(signedUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type
      }
    })

    if (!response.ok) {
      throw new Error(`Failed to upload picture: ${response.statusText}`)
    }
  } catch (error) {
    const logData = formatErrorForLogging(error)
    logger('Error uploading picture to S3:', logData)
    throw error
  }
}
